import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { OutboundFaxImport } from 'app/core/models/outbound-fax-import.model';
import { OutboundFaxImportsService } from 'app/core/services/outbound-fax-imports.service';
import { DownloadService } from 'app/core/services/download.service';
import { JobStatus } from 'app/core/enums/job-status.enum';
import { LimitOffsetPaging } from 'app/core/models/paged-results/limit-offset-paging.model';
import { ImportNavComponent } from '../../../shared/components/import-nav/import-nav.component';
import { DecimalPipe, TitleCasePipe, DatePipe } from '@angular/common';
import { PaginationHeaderComponent } from '../../../shared/components/pagination-header/pagination-header.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbDropdown, NgbDropdownToggle, NgbDropdownMenu } from '@ng-bootstrap/ng-bootstrap';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-capture-admin-import-outbound-faxes-history',
    templateUrl: './capture-admin-import-outbound-faxes-history.component.html',
    styleUrls: ['./capture-admin-import-outbound-faxes-history.component.scss'],
    imports: [ImportNavComponent,
              PaginationHeaderComponent,
              FaIconComponent,
              NgbDropdown,
              NgbDropdownToggle,
              NgbDropdownMenu,
              PaginationComponent,
              MatProgressSpinner,
              DecimalPipe,
              TitleCasePipe,
              DatePipe]
})
export class CaptureAdminImportOutboundFaxesHistoryComponent implements OnInit, OnDestroy {
  private outboundFaxImportsService = inject(OutboundFaxImportsService);
  private downloadService = inject(DownloadService);

  loading = false;
  outboundFaxImports: OutboundFaxImport[] = [];
  nextCheckTimeout = null;
  paging: LimitOffsetPaging;

  ngOnInit() {
    this.paging = LimitOffsetPaging.empty;
    this.paging.currentPage = 1;
    this.loadImports(this.paging.currentPage);
  }

  ngOnDestroy() {
    clearTimeout(this.nextCheckTimeout);
  }

  onPageSizeChange(size: number) {
    this.paging.pageSize = size;
    this.loadImports(this.paging.currentPage);
  }

  onPageChange(page: number) {
    this.loadImports(page);
  }

  onCancelClick($event: MouseEvent, outboundFaxImport: OutboundFaxImport) {
    $event.preventDefault();
    this.outboundFaxImportsService.cancel(outboundFaxImport.id).subscribe(faxImport => {
      const index = this.outboundFaxImports.findIndex(imp => imp.id === faxImport.id);

      if (index > -1) {
        this.outboundFaxImports[index] = faxImport;
        this.outboundFaxImports = [...this.outboundFaxImports];
      }
    });
  }

  onDownloadOriginalClick($event: MouseEvent, outboundFaxImport: OutboundFaxImport) {
    $event.preventDefault();
    this.outboundFaxImportsService.get(outboundFaxImport.id).subscribe(faxImport => {
      this.downloadService.getDownloadUrl(faxImport.fileExpirableDownloadPath).subscribe(expirableDownload => {
        this.downloadService.download(expirableDownload.url);
      });
    });
  }

  onDownloadSkippedClick($event: MouseEvent, outboundFaxImport: OutboundFaxImport) {
    $event.preventDefault();
    this.outboundFaxImportsService.generateSkippedFile(outboundFaxImport.id).subscribe(({ skippedFile }) => {
      if (skippedFile.url) {
        this.downloadService.download(skippedFile.url);
      } else {
        alert('Failed to generate file.');
      }
    });
  }

  onDownloadOutcomesClick($event: MouseEvent, outboundFaxImport: OutboundFaxImport) {
    $event.preventDefault();
    this.outboundFaxImportsService.generateOutcomeFile(outboundFaxImport.id).subscribe(({ outcomeFile }) => {
      if (outcomeFile.url) {
        this.downloadService.download(outcomeFile.url);
      } else {
        alert('Failed to generate file.');
      }
    });
  }

  skippedAndOutcomeFilesAvailable(outboundFaxImport: OutboundFaxImport) {
    return outboundFaxImport.status !== JobStatus.initialized;
  }

  trackImportItemsBy(_index: number, item: OutboundFaxImport) {
    return `${item.id}-${item.status}-${item.totalRows}-${item.queued}-${item.skipped}-${item.scheduledWithRetries}-${item.scheduledWithoutRetries
      }`;
  }

  private loadImports(page, loading = true) {
    this.loading = loading;
    const paging = { page, pageSize: this.paging.pageSize };
    this.outboundFaxImportsService.getList(paging).subscribe(result => {
      this.outboundFaxImports = result.outboundFaxImports;
      this.paging = result.meta.paging;
      this.loading = false;

      const anyActive: boolean = this.outboundFaxImports.some(faxImport => faxImport.active);

      if (anyActive) {
        this.nextCheckTimeout = setTimeout(() => this.loadImports(this.paging.currentPage, false), 1250);
      }
    });
  }
}
